import type { CheatsheetItem } from "../lib/types";

/**
 * The full Git cheat sheet shown on the Sandbox page — a superset of the
 * per-lesson cheatsheets, grouped by theme. Every command listed here is one
 * the sandbox engine (`src/lib/sandbox-engine.ts`) understands, so learners can
 * copy a row straight into the terminal and watch it run.
 */
export interface CheatGroup {
  title: string;
  items: CheatsheetItem[];
}

export const CHEAT_GROUPS: CheatGroup[] = [
  {
    title: "The everyday loop",
    items: [
      { cmd: "git status", desc: "What state are my files in right now?" },
      { cmd: "git add <file>", desc: "Stage a file for the next commit." },
      { cmd: "git add .", desc: "Stage everything that changed." },
      { cmd: 'git commit -m "msg"', desc: "Make a commit with a message." },
      { cmd: 'git commit -am "msg"', desc: "Stage tracked changes and commit in one step." },
      { cmd: "git log --oneline", desc: "Show the commit history, one line each." },
      { cmd: "git diff", desc: "See what changed but isn't staged yet." },
      { cmd: "git diff --staged", desc: "See what's staged for the next commit." },
    ],
  },
  {
    title: "Branching & merging",
    items: [
      { cmd: "git branch", desc: "List branches (the * marks where you are)." },
      { cmd: "git branch <name>", desc: "Create a new branch at HEAD (doesn't switch)." },
      { cmd: "git switch <name>", desc: "Switch to an existing branch." },
      { cmd: "git switch -c <name>", desc: "Create a branch and switch to it." },
      { cmd: "git checkout <name>", desc: "Older way to switch branches / restore files." },
      { cmd: "git merge <name>", desc: "Merge the named branch into the current one." },
      { cmd: "git merge --abort", desc: "Back out of an in-progress (conflicted) merge." },
      { cmd: "git branch -d <name>", desc: "Delete a branch you no longer need." },
      { cmd: "git tag <name>", desc: "Mark the current commit with a name (e.g. v1.0)." },
    ],
  },
  {
    title: "Fixing & undoing",
    items: [
      { cmd: "git restore <file>", desc: "Throw away unstaged edits to a file. History untouched." },
      { cmd: "git restore --staged <file>", desc: "Unstage a file but keep your edits. Opposite of git add." },
      { cmd: "git reset --soft HEAD~1", desc: "Undo the last commit, keep changes staged." },
      { cmd: "git reset --mixed HEAD~1", desc: "Undo the last commit, keep changes unstaged (default)." },
      { cmd: "git reset --hard HEAD~1", desc: "Undo the last commit AND discard the changes. Destructive." },
      { cmd: "git revert <commit>", desc: "Add a new commit that inverts an old one. Safe for shared history." },
      { cmd: "git reflog", desc: "Every move HEAD has made — your recovery log." },
      { cmd: "git reset --hard HEAD@{1}", desc: "Rewind to where HEAD was N moves ago. Rescues 'lost' commits." },
      { cmd: "git stash", desc: "Shelve your changes to come back to a clean tree." },
      { cmd: "git stash pop", desc: "Re-apply the most recently stashed changes." },
    ],
  },
  {
    title: "Setup & inspection",
    items: [
      { cmd: "git init", desc: "Turn the current folder into a git repository." },
      { cmd: 'git config user.name "You"', desc: "Set the name stamped on your commits." },
      { cmd: "git remote -v", desc: "List the remotes (URLs) configured for this repo." },
      { cmd: "git log --oneline --all", desc: "See history across every branch, not just yours." },
      { cmd: "git rm <file>", desc: "Stop tracking a file and delete it." },
      { cmd: "git rm --cached <file>", desc: "Stop tracking a file but keep it on disk." },
    ],
  },
];
